import React, { useState, useEffect } from 'react';
import {
    LineChart, Line, XAxis, YAxis,
    CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    ReferenceLine
} from 'recharts';


// Import pricing data - using copies from the Cost Calculator project
import GooglePricing from './data/Pricing/Google/pricing.json';
import IBMPricing from './data/Pricing/IBM/pricing.json';
import MicrosoftPricing from './data/Pricing/Microsoft/pricing.json';
import AnthropicPricing from './data/Pricing/Anthropic/pricing.json';

// Function to format currency values
const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 4,
        maximumFractionDigits: 4
    }).format(value);
};

const TokenCostVisualization = () => {
    // Add a forceUpdate mechanism for redrawing
    const [updateKey, setUpdateKey] = useState(0);
    const forceUpdate = () => setUpdateKey(key => key + 1);
    
    // Simulation parameters with localStorage
    const [userInputTokens, setUserInputTokens] = useState(() => {
        const saved = localStorage.getItem('tokenCalc_userInputTokens');
        return saved ? Number(saved) : 700;
    });
    const [systemContextTokens, setSystemContextTokens] = useState(() => {
        const saved = localStorage.getItem('tokenCalc_systemContextTokens');
        return saved ? Number(saved) : 6000;
    });
    const [tokensInResponse, setTokensInResponse] = useState(() => {
        const saved = localStorage.getItem('tokenCalc_tokensInResponse');
        return saved ? Number(saved) : 1500;
    });
    const percentResponseInSubsequent = 100; // Always use 100% in calculations
    const [maxTurns, setMaxTurns] = useState(() => {
        const saved = localStorage.getItem('tokenCalc_maxTurns');
        return saved ? Number(saved) : 10;
    });
    const [currentTurn, setCurrentTurn] = useState(10); // Start with all turns visible
    // Always show formulas
    const [highlight, setHighlight] = useState(false);
    const [isSessionTotalsExpanded, setIsSessionTotalsExpanded] = useState(true); // For collapsible session totals

    // Draggable position for parameters div
    const [paramsPosition, setParamsPosition] = useState(() => {
        const saved = localStorage.getItem('tokenCalc_paramsPosition');
        return saved ? JSON.parse(saved) : { x: 0, y: 20 };
    });
    const [isParamsDragging, setIsParamsDragging] = useState(false);
    const [paramsDragStart, setParamsDragStart] = useState({ x: 0, y: 0 });

    // Provider and model selection state with localStorage
    const [selectedProvider, setSelectedProvider] = useState(() => {
        const saved = localStorage.getItem('tokenCalc_selectedProvider');
        return saved || 'Microsoft';
    });
    const [selectedModel, setSelectedModel] = useState(() => {
        const saved = localStorage.getItem('tokenCalc_selectedModel');
        return saved || 'GPT-4o Global Deployment';
    });
    const [availableModels, setAvailableModels] = useState([]);
    const [modelPricing, setModelPricing] = useState({
        inputCostPer1MTokens: 0.5,
        outputCostPer1MTokens: 1.5
    });

    // Combined pricing data from all providers
    const allPricingData = [...GooglePricing, ...IBMPricing, ...MicrosoftPricing, ...AnthropicPricing];

    // Get unique providers from the pricing data
    const providers = [...new Set(allPricingData.map(item => item.provider))].sort();

    // Save parameter values to localStorage
    useEffect(() => {
        localStorage.setItem('tokenCalc_userInputTokens', userInputTokens.toString());
    }, [userInputTokens]);
    useEffect(() => {
        localStorage.setItem('tokenCalc_systemContextTokens', systemContextTokens.toString());
    }, [systemContextTokens]);
    useEffect(() => {
        localStorage.setItem('tokenCalc_tokensInResponse', tokensInResponse.toString());
    }, [tokensInResponse]);
    useEffect(() => {
        localStorage.setItem('tokenCalc_maxTurns', maxTurns.toString());
    }, [maxTurns]);

    // Save provider and model selection to localStorage
    useEffect(() => {
        localStorage.setItem('tokenCalc_selectedProvider', selectedProvider);
    }, [selectedProvider]);
    useEffect(() => {
        localStorage.setItem('tokenCalc_selectedModel', selectedModel);
    }, [selectedModel]);

    // Save parameters position to localStorage
    useEffect(() => {
        localStorage.setItem('tokenCalc_paramsPosition', JSON.stringify(paramsPosition));
    }, [paramsPosition]);

    // Drag handlers for parameters div
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isParamsDragging) {
                const newX = e.clientX - paramsDragStart.x;
                const newY = e.clientY - paramsDragStart.y;
                setParamsPosition({ x: newX, y: newY });
            }
        };

        const handleMouseUp = () => {
            if (isParamsDragging) {
                setIsParamsDragging(false);
                console.log('Parameters Position:', paramsPosition);
            }
        };

        if (isParamsDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isParamsDragging, paramsDragStart]);

    const handleParamsMouseDown = (e) => {
        setIsParamsDragging(true);
        setParamsDragStart({
            x: e.clientX - paramsPosition.x,
            y: e.clientY - paramsPosition.y
        });
    };

    // Update available models when provider changes
    useEffect(() => {
        const providerModels = allPricingData
            .filter(item => item.provider === selectedProvider)
            .sort((a, b) => (a.order || 0) - (b.order || 0));

        setAvailableModels(providerModels);

        // Set first model as default if not already selected
        if (providerModels.length > 0 && (!selectedModel || !providerModels.find(m => m.model === selectedModel))) {
            setSelectedModel(providerModels[0].model);
            setModelPricing({
                inputCostPer1MTokens: providerModels[0].inputCostPer1MTokens || 0,
                outputCostPer1MTokens: providerModels[0].outputCostPer1MTokens || 0
            });
        }
    }, [selectedProvider]);

    // Update pricing when model changes
    useEffect(() => {
        const modelData = allPricingData.find(item =>
            item.provider === selectedProvider && item.model === selectedModel);

        if (modelData) {
            // Handle both 1M and 1K token pricing formats
            const inputCost = modelData.inputCostPer1MTokens || (modelData.inputCostPer1KTokens * 1000) || 0;
            const outputCost = modelData.outputCostPer1MTokens || (modelData.outputCostPer1KTokens * 1000) || 0;

            setModelPricing({
                inputCostPer1MTokens: inputCost,
                outputCostPer1MTokens: outputCost
            });
        }
    }, [selectedModel, selectedProvider]);

    // Generate data based on your updated Excel formula
    const generateTokenData = () => {
        const data = [];

        for (let turn = 1; turn <= maxTurns; turn++) {
            // Calculate tokens for this specific turn
            let tokensThisTurn = 0;
            if (turn === 1) {
                // First turn is just the initial prompt + system context
                tokensThisTurn = systemContextTokens + userInputTokens;
            } else {
                // Subsequent turns include system context + user input + history
                // History includes previous user inputs and responses, but NOT system context
                const historyTokens = (turn - 1) * userInputTokens + // Previous user inputs
                    (turn - 1) * tokensInResponse * (percentResponseInSubsequent / 100); // Previous responses

                tokensThisTurn = systemContextTokens + userInputTokens + historyTokens;
            }

            // Calculate total session tokens if session ended at this turn
            // Using your updated formula: S*N + U*N + (U+R)*(N*(N-1))/2
            const r = turn; // number of requests
            const s = systemContextTokens;
            const u = userInputTokens;
            const tir = tokensInResponse;
            const prs = percentResponseInSubsequent / 100;

            const totalInputTokensFormula = s * r + u * r + (u + tir * prs) * (r * (r - 1)) / 2;
            const totalOutputTokensFormula = tir * r;

            // Calculate costs based on selected model pricing
            const inputCost = (totalInputTokensFormula / 1000000) * modelPricing.inputCostPer1MTokens;
            const outputCost = (totalOutputTokensFormula / 1000000) * modelPricing.outputCostPer1MTokens;
            const totalCost = inputCost + outputCost;

            // Create data point
            data.push({
                turn,
                tokensThisTurn,
                tokensInResponseThisTurn: tokensInResponse,
                totalInputTokensFormula: turn <= currentTurn ? totalInputTokensFormula : null,
                totalOutputTokensFormula: turn <= currentTurn ? totalOutputTokensFormula : null,
                inputCost: turn <= currentTurn ? inputCost : null,
                outputCost: turn <= currentTurn ? outputCost : null,
                totalCost: turn <= currentTurn ? totalCost : null,
                active: turn <= currentTurn,
                currentlyActive: turn === currentTurn
            });
        }

        return data;
    };

    const [data, setData] = useState(generateTokenData());

    // Update data when parameters change
    useEffect(() => {
        // Generate fresh data and force complete reset
        const newData = generateTokenData();
        setData(newData);
        
        // Always show all turns
        setCurrentTurn(maxTurns);
        
        // Force a redraw by updating the key
        forceUpdate();
    }, [userInputTokens, tokensInResponse, systemContextTokens, selectedProvider, selectedModel, modelPricing, maxTurns]);
    
    // Initialize chart data when component mounts
    useEffect(() => {
        // Show the full chart immediately (all turns visible)
        setCurrentTurn(maxTurns);
    }, [userInputTokens, tokensInResponse, systemContextTokens, selectedProvider, selectedModel, modelPricing, maxTurns]);

    // Update current turn when maxTurns changes
    useEffect(() => {
        setCurrentTurn(maxTurns);
    }, [maxTurns]);

    // Calculate total billed tokens and costs for the current session
    const calculateCurrentTotals = () => {
        // Using your updated Excel formula for total billed tokens
        const r = currentTurn; // number of requests
        const s = systemContextTokens;
        const u = userInputTokens;
        const tir = tokensInResponse;
        const prs = percentResponseInSubsequent / 100;

        const totalInputTokens = s * r + u * r + (u + tir * prs) * (r * (r - 1)) / 2;
        const totalOutputTokens = tir * r;

        // Calculate costs based on selected model pricing
        const inputCost = (totalInputTokens / 1000000) * modelPricing.inputCostPer1MTokens;
        const outputCost = (totalOutputTokens / 1000000) * modelPricing.outputCostPer1MTokens;
        const totalCost = inputCost + outputCost;

        return {
            totalInputTokens,
            totalOutputTokens,
            grandTotal: totalInputTokens + totalOutputTokens,
            inputCost,
            outputCost,
            totalCost
        };
    };

    // Custom tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-4 border rounded shadow-lg">
                    <p className="text-lg font-bold mb-2">Turn {label}</p>
                    <div className="space-y-1">
                        <p>                                <span className="font-semibold">Input Tokens This Turn:</span> {payload[0].payload.tokensThisTurn.toLocaleString()}</p>
                        <p><span className="font-semibold">Output Tokens This Turn:</span> {payload[0].payload.tokensInResponseThisTurn.toLocaleString()}</p>
                        <div className="border-t border-gray-200 my-2 pt-2">
                            <p>
                                <span className="font-semibold">Total Session Input Tokens:</span> {' '}
                                {payload[0].payload.totalInputTokensFormula ?
                                    payload[0].payload.totalInputTokensFormula.toLocaleString() : 'N/A'}
                            </p>
                            <p>
                                <span className="font-semibold">Total Session Output Tokens:</span> {' '}
                                {payload[0].payload.totalOutputTokensFormula ?
                                    payload[0].payload.totalOutputTokensFormula.toLocaleString() : 'N/A'}
                            </p>
                            <div className="border-t border-gray-200 my-2 pt-2 text-green-700 font-semibold">
                                <p>
                                    <span className="font-semibold">Input Cost:</span> {' '}
                                    {payload[0].payload.inputCost ?
                                        formatCurrency(payload[0].payload.inputCost) : 'N/A'}
                                </p>
                                <p>
                                    <span className="font-semibold">Output Cost:</span> {' '}
                                    {payload[0].payload.outputCost ?
                                        formatCurrency(payload[0].payload.outputCost) : 'N/A'}
                                </p>
                                <p className="font-bold text-green-600 mt-1">
                                    Total Cost: {payload[0].payload.totalCost ?
                                        formatCurrency(payload[0].payload.totalCost) :
                                        'N/A'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    };

    // Generate conversation simulation data
    const conversationData = () => {
        const conversation = [];

        // System context (shown as a separate message for clarity)
        conversation.push({
            role: 'system',
            content: `You are a helpful AI assistant. Your task is to provide helpful, accurate, and ethical responses.`,
            tokens: systemContextTokens,
            isVisible: true,
            isSystemContext: true
        });

        // User queries and AI responses
        for (let i = 1; i <= maxTurns; i++) {
            // Calculate history tokens for this turn
            let historyTokens = 0;
            if (i > 1) {
                historyTokens = (i - 1) * userInputTokens + // Previous user inputs
                    (i - 1) * tokensInResponse * (percentResponseInSubsequent / 100); // Previous responses
            }

            // User message
            conversation.push({
                role: 'user',
                content: `Question ${i}: Can you help me with...`,
                tokens: userInputTokens,
                historyTokens: historyTokens,
                totalTokens: systemContextTokens + userInputTokens + historyTokens,
                isVisible: i <= currentTurn,
                isCurrent: i === currentTurn
            });

            // AI response
            conversation.push({
                role: 'assistant',
                content: `Response ${i}: I'd be happy to help! Here's the information you requested...`,
                tokens: tokensInResponse,
                isVisible: i <= currentTurn,
                isCurrent: i === currentTurn
            });
        }

        return conversation;
    };

    // Format large numbers
    const formatNumber = (num) => {
        return num?.toLocaleString() || 'N/A';
    };

    // Calculate current totals
    const currentTotals = calculateCurrentTotals();

    // Calculate cost if each token costs $0.01 per 1000 tokens (for demonstration)
    const estimatedCost = (currentTotals.grandTotal / 1000) * 0.01;

    return (

        <div className="flex flex-col w-full">

            {/* Placeholder for old Totals section - removed */}

            <div className="text-center mb-6">
                {/* Title and subtitle removed - now only in App.js header */}
            </div>


            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Main chart */}
                <div className="bg-white p-4 rounded-lg shadow-md h-96">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="text-lg font-semibold">Cost by Turn: {selectedProvider} - {selectedModel}</h3>
                        <div className={`text-lg transition-all duration-300 ${highlight ? 'text-xl text-yellow-500 font-bold scale-110' : ''}`}>
                            Turn: <span className={`font-bold ${highlight ? 'text-blue-600' : ''}`}>{currentTurn}</span> of {maxTurns}
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height="90%" key={`chart-container-${updateKey}`}>
                        <LineChart 
                            data={data} 
                            margin={{ top: 5, right: 30, left: 35, bottom: 15 }}
                            key={`linechart-${updateKey}`}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="turn"
                                label={{ value: 'Conversation Turn', position: 'bottom', offset: 0 }}
                            />
                            <YAxis
                                label={{ value: 'Cost ($)', angle: -90, position: 'insideLeft', offset: -25 }}
                                tickFormatter={(value) => formatCurrency(value)}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend
                                verticalAlign="top"
                                height={36}
                                wrapperStyle={{ paddingTop: "10px" }}
                            />
                            <Line
                                type="monotone"
                                dataKey="inputCost"
                                name="Input Cost"
                                stroke="#8884d8"
                                activeDot={{ r: 8 }}
                                strokeWidth={2}
                                dot={(props) => {
                                    const { cx, cy, payload } = props;
                                    // Create a unique key using index if turn is undefined
                                    const turnKey = payload.turn || props.index || 0;
                                    return payload.currentlyActive ? (
                                        <circle
                                            key={`input-active-${turnKey}`}
                                            cx={cx}
                                            cy={cy}
                                            r={6}
                                            fill="#ff5722"
                                            stroke="#ff5722"
                                            className={highlight ? 'animate-ping' : ''}
                                        />
                                    ) : (
                                        <circle
                                            key={`input-${turnKey}`}
                                            cx={cx}
                                            cy={cy}
                                            r={payload.active ? 4 : 0}
                                            fill="#8884d8"
                                        />
                                    );
                                }}
                            />

                            <Line
                                type="monotone"
                                dataKey="outputCost"
                                name="Output Cost"
                                stroke="#82ca9d"
                                strokeWidth={2}
                                dot={(props) => {
                                    const { cx, cy, payload } = props;
                                    // Create a unique key using index if turn is undefined
                                    const turnKey = payload.turn || props.index || 0;
                                    return payload.currentlyActive ? (
                                        <circle
                                            key={`output-active-${turnKey}`}
                                            cx={cx}
                                            cy={cy}
                                            r={6}
                                            fill="#ff9800"
                                            stroke="#ff9800"
                                            className={highlight ? 'animate-ping' : ''}
                                        />
                                    ) : (
                                        <circle
                                            key={`output-${turnKey}`}
                                            cx={cx}
                                            cy={cy}
                                            r={payload.active ? 4 : 0}
                                            fill="#82ca9d"
                                        />
                                    );
                                }}
                            />

                            <Line
                                type="monotone"
                                dataKey="totalCost"
                                name="Total Cost"
                                stroke="#ff5722"
                                strokeWidth={3}
                                dot={(props) => {
                                    const { cx, cy, payload } = props;
                                    // Create a unique key using index if turn is undefined
                                    const turnKey = payload.turn || props.index || 0;
                                    return payload.currentlyActive ? (
                                        <circle
                                            key={`total-active-${turnKey}`}
                                            cx={cx}
                                            cy={cy}
                                            r={7}
                                            fill="#ff5722"
                                            stroke="#ff5722"
                                            className={highlight ? 'animate-ping' : ''}
                                        />
                                    ) : (
                                        <circle
                                            key={`total-${turnKey}`}
                                            cx={cx}
                                            cy={cy}
                                            r={payload.active ? 5 : 0}
                                            fill="#ff5722"
                                        />
                                    );
                                }}
                            />

                            {/* Reference line for current turn */}
                            <ReferenceLine x={currentTurn} stroke="#ff9800" strokeWidth={2} strokeDasharray="5 5" />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Conversation and calculation breakdown */}
                <div className="bg-white p-4 rounded-lg shadow-md h-96 overflow-auto">
                    <h3 className="text-lg font-semibold mb-2">Cost Calculation Breakdown</h3>
                    
                    {/* Session Totals moved to top of calculation breakdown */}
                    <div className="mb-5 p-3 bg-blue-50 rounded-lg border border-blue-300">
                        <div className="flex justify-between items-center">
                            <h4 className="font-bold text-blue-700">Session Totals (Turn {currentTurn})</h4>
                            <button
                                onClick={() => setIsSessionTotalsExpanded(!isSessionTotalsExpanded)}
                                className="text-blue-600 text-lg font-bold"
                                type="button"
                            >
                                {isSessionTotalsExpanded ? '▲' : '▼'}
                            </button>
                        </div>

                        {isSessionTotalsExpanded && (
                            <div className="mt-2">
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                                    <div className="font-semibold">Input Tokens:</div>
                                    <div className={`${highlight ? 'text-blue-600 font-bold' : ''}`}>
                                        {currentTotals.totalInputTokens.toLocaleString()}
                                    </div>

                                    <div className="font-semibold">Output Tokens:</div>
                                    <div>{currentTotals.totalOutputTokens.toLocaleString()}</div>

                                    <div className="font-semibold col-span-2 pt-2 border-t border-gray-200 mt-1">Total Tokens:</div>
                                    <div className="col-span-2 text-base font-bold text-blue-600">
                                        {currentTotals.grandTotal.toLocaleString()} tokens
                                    </div>

                                    <div className="font-semibold col-span-2 pt-2 border-t border-gray-200 mt-1">Costs:</div>
                                    <div className="font-semibold">Input Cost:</div>
                                    <div className="text-blue-600">{formatCurrency(currentTotals.inputCost)}</div>

                                    <div className="font-semibold">Output Cost:</div>
                                    <div className="text-blue-600">{formatCurrency(currentTotals.outputCost)}</div>

                                    <div className="font-semibold col-span-2 pt-1 mt-1">Total Cost:</div>
                                    <div className="col-span-2 text-base font-bold text-green-600">
                                        {formatCurrency(currentTotals.totalCost)}
                                    </div>

                                    <div className="col-span-2 text-xs text-gray-500 mt-1">
                                        Based on {selectedProvider} {selectedModel} pricing
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {currentTurn > 0 && (
                        <div className={`mb-4 p-3 rounded border ${highlight ? 'bg-yellow-50 border-yellow-300' : 'bg-blue-50 border-blue-200'} transition-colors duration-300`}>
                            <h4 className="font-bold">Current Turn {currentTurn} Calculation:</h4>
                            <div className="text-sm font-mono mt-2 p-2 bg-gray-100 rounded">
                                <p>r = {currentTurn} (turns)</p>
                                <p>s = {systemContextTokens} (system context tokens)</p>
                                <p>u = {userInputTokens} (user input tokens)</p>
                                <p>tir = {tokensInResponse} (tokens in response)</p>
                                <p>prs = {percentResponseInSubsequent / 100} (% response in subsequent)</p>
                                <p className="mt-2">Total Input Tokens = s*r + u*r + (u+tir*prs)*(r*(r-1))/2</p>
                                <p>= {systemContextTokens}*{currentTurn} + {userInputTokens}*{currentTurn} + ({userInputTokens}+{tokensInResponse}*{percentResponseInSubsequent / 100})*({currentTurn}*({currentTurn}-1))/2</p>
                                <p className="font-bold">= {currentTotals.totalInputTokens.toLocaleString()}</p>
                                <p className="mt-2">Total Output Tokens = tir * r</p>
                                <p>= {tokensInResponse} * {currentTurn}</p>
                                <p className="font-bold">= {currentTotals.totalOutputTokens.toLocaleString()}</p>

                                <div className="mt-3 pt-2 border-t border-gray-300">
                                    <p className="font-bold">Cost Calculation:</p>
                                    <p>Input Cost = (Input Tokens / 1M) * ${modelPricing.inputCostPer1MTokens.toFixed(4)}</p>
                                    <p>= ({currentTotals.totalInputTokens.toLocaleString()} / 1,000,000) * ${modelPricing.inputCostPer1MTokens.toFixed(4)}</p>
                                    <p className="font-bold">= {formatCurrency(currentTotals.inputCost)}</p>

                                    <p className="mt-2">Output Cost = (Output Tokens / 1M) * ${modelPricing.outputCostPer1MTokens.toFixed(4)}</p>
                                    <p>= ({currentTotals.totalOutputTokens.toLocaleString()} / 1,000,000) * ${modelPricing.outputCostPer1MTokens.toFixed(4)}</p>
                                    <p className="font-bold">= {formatCurrency(currentTotals.outputCost)}</p>

                                    <p className="mt-2">Total Cost = Input Cost + Output Cost</p>
                                    <p>= {formatCurrency(currentTotals.inputCost)} + {formatCurrency(currentTotals.outputCost)}</p>
                                    <p className="font-bold">= {formatCurrency(currentTotals.totalCost)}</p>
                                </div>
                            </div>
                            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1">
                                <div className="font-semibold">Input Tokens:</div>
                                <div>{currentTotals.totalInputTokens.toLocaleString()}</div>
                                <div className="font-semibold">Output Tokens:</div>
                                <div>{currentTotals.totalOutputTokens.toLocaleString()}</div>
                                <div className="font-semibold pt-1 border-t border-gray-200 mt-1">Total Tokens:</div>
                                <div className="font-bold border-t border-gray-200 mt-1">{currentTotals.grandTotal.toLocaleString()}</div>

                                <div className="font-semibold pt-3 text-green-700">Input Cost:</div>
                                <div className="pt-3">{formatCurrency(currentTotals.inputCost)}</div>
                                <div className="font-semibold text-green-700">Output Cost:</div>
                                <div>{formatCurrency(currentTotals.outputCost)}</div>
                                <div className="font-semibold pt-1 text-green-700">Total Cost:</div>
                                <div className="font-bold">{formatCurrency(currentTotals.totalCost)}</div>
                            </div>
                        </div>
                    )}

                    <div className="space-y-3">
                        {/* First display system context message */}
                        {conversationData().filter(m => m.isSystemContext).map((message, index) => (
                            <div
                                key={`system-${index}`}
                                className={`p-3 rounded-lg bg-purple-100 text-left ${highlight && currentTurn === 1 ? 'border-2 border-yellow-500 shadow-md' : ''} transition-all duration-300`}
                            >
                                <div className="flex justify-between items-center mb-1">
                                    <span className="font-semibold">System Context</span>
                                    <span className={`text-xs px-2 py-1 rounded ${highlight && currentTurn === 1 ? 'bg-yellow-200 font-bold' : 'bg-gray-200'}`}>
                                        {message.tokens.toLocaleString()} tokens
                                    </span>
                                </div>
                                <p className="text-sm">{message.content}</p>
                            </div>
                        ))}

                        {/* Then display user/assistant conversation */}
                        {conversationData().filter(m => m.isVisible && !m.isSystemContext).map((message, index) => (
                            <div
                                key={index}
                                className={`p-3 rounded-lg ${message.role === 'user'
                                    ? 'bg-blue-100 text-left'
                                    : 'bg-green-100 text-left'
                                    } ${message.isCurrent ? `border-2 ${highlight ? 'border-yellow-500 shadow-md' : 'border-blue-500'}` : ''} transition-all duration-300`}
                            >
                                <div className="flex justify-between items-center mb-1">
                                    <span className="font-semibold">
                                        {message.role === 'user' ? `User (Turn ${Math.ceil((index) / 2 + 0.5)})` : 'AI Assistant'}
                                    </span>
                                    <span className={`text-xs px-2 py-1 rounded ${message.isCurrent && highlight ? 'bg-yellow-200 font-bold' : 'bg-gray-200'}`}>
                                        {message.role === 'user' ? (
                                            <>
                                                {message.tokens.toLocaleString()} tokens
                                                {message.historyTokens > 0 && (
                                                    <span className="ml-1 text-gray-600">
                                                        + {message.historyTokens.toLocaleString()} history
                                                    </span>
                                                )}
                                            </>
                                        ) : (
                                            <>{message.tokens.toLocaleString()} tokens</>
                                        )}
                                    </span>
                                </div>
                                <p className="text-sm">{message.content}</p>
                                {message.role === 'user' && (
                                    <div className="flex flex-col mt-2 text-sm bg-white bg-opacity-50 rounded-md p-2">
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-700">User Input: </span>
                                            <span className="font-medium text-blue-600">{message.tokens.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-700">System Context: </span>
                                            <span className="font-medium text-purple-600">{systemContextTokens.toLocaleString()}</span>
                                        </div>
                                        {message.historyTokens > 0 && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-700">History: </span>
                                                <span className="font-medium text-amber-600">{message.historyTokens.toLocaleString()}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between items-center border-t border-gray-200 mt-1 pt-1">
                                            <span className="font-semibold text-gray-800">Total: </span>
                                            <span className="font-bold text-gray-900">{message.totalTokens.toLocaleString()}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Floating Draggable Parameters Div */}
            <div
                className="fixed p-3 bg-white rounded-lg border-2 border-gray-300 shadow-lg z-50"
                style={{
                    left: `${paramsPosition.x}px`,
                    top: `${paramsPosition.y}px`,
                    cursor: isParamsDragging ? 'grabbing' : 'grab',
                    width: '320px'
                }}
            >
                <div
                    className="bg-gray-200 px-3 py-2 -mx-3 -mt-3 mb-2 rounded-t-lg cursor-move border-b-2 border-gray-400"
                    onMouseDown={handleParamsMouseDown}
                >
                    <h3 className="text-xs font-semibold text-gray-700 flex items-center">
                        <span className="mr-2">⋮⋮</span>
                        🎛️ Parameters & Model
                        <span className="ml-auto text-[10px] text-gray-500 italic">(drag to move)</span>
                    </h3>
                </div>
                <div className="space-y-2 text-xs">
                    {/* Provider and Model Selection */}
                    <div>
                        <label className="font-medium block mb-1">Provider:</label>
                        <select
                            value={selectedProvider}
                            onChange={(e) => setSelectedProvider(e.target.value)}
                            className="w-full p-1 border rounded text-xs"
                        >
                            {providers.map(provider => (
                                <option key={provider} value={provider}>
                                    {provider}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="font-medium block mb-1">Model:</label>
                        <select
                            value={selectedModel}
                            onChange={(e) => setSelectedModel(e.target.value)}
                            className="w-full p-1 border rounded text-xs"
                        >
                            {availableModels.map(model => (
                                <option key={model.model} value={model.model}>
                                    {model.model}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Pricing Info */}
                    <div className="bg-blue-50 p-2 rounded border border-blue-200">
                        <div className="flex justify-between items-center mb-1">
                            <span className="text-gray-600">Input Cost:</span>
                            <span className="font-medium">${modelPricing.inputCostPer1MTokens.toFixed(4)}/1M</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-600">Output Cost:</span>
                            <span className="font-medium">${modelPricing.outputCostPer1MTokens.toFixed(4)}/1M</span>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-300 my-2"></div>

                    {/* Token Parameters */}
                    <div className="flex items-center justify-between">
                        <label className="font-medium w-40">User Input Tokens:</label>
                        <input
                            type="number"
                            defaultValue={userInputTokens}
                            onBlur={(e) => {
                                const val = parseInt(e.target.value) || 0;
                                setUserInputTokens(val);
                                const newData = generateTokenData();
                                setData(newData);
                                setCurrentTurn(maxTurns);
                                forceUpdate();
                            }}
                            className="w-24 p-1 border rounded text-xs text-right"
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <label className="font-medium w-40">System Context Tokens:</label>
                        <input
                            type="number"
                            defaultValue={systemContextTokens}
                            onBlur={(e) => {
                                const val = parseInt(e.target.value) || 0;
                                setSystemContextTokens(val);
                                const newData = generateTokenData();
                                setData(newData);
                                setCurrentTurn(maxTurns);
                                forceUpdate();
                            }}
                            className="w-24 p-1 border rounded text-xs text-right"
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <label className="font-medium w-40">Tokens In Response:</label>
                        <input
                            type="number"
                            defaultValue={tokensInResponse}
                            onBlur={(e) => {
                                const val = parseInt(e.target.value) || 0;
                                setTokensInResponse(val);
                                const newData = generateTokenData();
                                setData(newData);
                                setCurrentTurn(maxTurns);
                                forceUpdate();
                            }}
                            className="w-24 p-1 border rounded text-xs text-right"
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <label className="font-medium w-40">Max Turns:</label>
                        <input
                            type="number"
                            defaultValue={maxTurns}
                            onBlur={(e) => {
                                const val = parseInt(e.target.value) || 1;
                                setMaxTurns(val);
                                const newData = generateTokenData();
                                setData(newData);
                                setCurrentTurn(val);
                                forceUpdate();
                            }}
                            className="w-24 p-1 border rounded text-xs text-right"
                        />
                    </div>
                </div>
            </div>

            <div className="mt-6 p-4 bg-white rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-2">Formula Details</h3>
                <div className="space-y-3">
                    <div className="p-3 bg-blue-50 rounded-lg">
                        <h4 className="font-semibold mb-1">Token Count Formula with System/User Input Split:</h4>
                        <div className="font-mono text-sm p-2 bg-white rounded border">
                            Total Input Tokens = (S * T) + (U * T) + ((U + R) * (T * (T - 1)) / 2)
                        </div>
                        <div className="mt-2 text-sm">
                            <span className="font-semibold">Where:</span>
                            <ul className="mt-1 pl-5 list-disc space-y-1">
                                <li><strong>S</strong> = System context tokens (sent with every request)</li>
                                <li><strong>U</strong> = User input tokens per request</li>
                                <li><strong>R</strong> = Response tokens</li>
                                <li><strong>T</strong> = Number of turns (requests)</li>
                            </ul>
                        </div>
                    </div>

                    <div className="p-3 bg-green-50 rounded-lg">
                        <h4 className="font-semibold mb-1">Explanation:</h4>
                        <ol className="mt-1 pl-5 list-decimal space-y-2 text-sm">
                            <li><strong>System Context Part (S * T):</strong> System tokens sent with every request but not accumulated in history</li>
                            <li><strong>User Input Part (U * T):</strong> User tokens sent with every request</li>
                            <li><strong>History Part ((U + R) * (T * (T-1)) / 2):</strong> Accumulated conversation history from previous turns</li>
                        </ol>
                    </div>

                    <div className="p-3 bg-yellow-50 rounded-lg">
                        <h4 className="font-semibold mb-1">Key Insight:</h4>
                        <p className="text-sm">
                            Moving tokens from user input to system context is more efficient because system context isn't duplicated in history.
                            System tokens appear only once per request, while user tokens get duplicated in subsequent requests.
                        </p>
                    </div>
                </div>
            </div>
            <div className="mt-6 text-center italic text-gray-600">
                <p>
                    "Notice how by turn {maxTurns}, the cost has grown dramatically.
                    Different LLM providers and models have significantly different pricing structures,
                    impacting the total cost of your conversational AI applications. The non-linear growth
                    of token usage translates directly to cost increases at scale, making pricing model
                    selection a critical business decision."
                </p>
            </div>
        </div>
    );
};

export default TokenCostVisualization;